import type { UiLang } from "../../lib/i18n";
import { useEffect, useState, type ComponentProps, type ReactNode } from "react";
import { SideNav } from "./SideNav";
import { SearchPalette } from "./SearchPalette";
import { TopBar } from "./TopBar";
import { docUrlOf, isSingle, navWithTiles } from "../../lib/nav";
import { useT, type MsgKey } from "../../lib/i18n";
import type { Tile } from "../../lib/tiles";
import type { Agent } from "../../lib/api";
import { LENS_PANEL_W } from "../apilens/ApiLens";
import { useLens } from "../../lib/apiLensStore";
import { HelpVideo } from "../ui/HelpVideo";
import { Notice } from "../ui/Notice";
import { enteredViaZhongtai, zhongtaiSunsetDays } from "../../lib/session";
import { lessonsForPage } from "../../lib/helpVideos";

/**
 * 最外层：顶栏 + 左菜单 + 内容区。
 *
 * 配置台是独立站点（不再嵌 super-admin），所以外壳由自己渲染；
 * 视觉按 super-admin 的设计 token 对齐，运营在两边切换时不割裂。
 */
export function AppShell({
  page, onPage, tiles, selectors, getSearchContent, isSuperAdmin, isRoot, agent, agents, onAgent, uiLang, onUiLang, user, children, onOpenLog, onLogout, env,
}: {
  page: string; onPage: (id: string) => void;
  /** 本版本的玩法格：菜单与面包屑要认种子之外的格子 */
  tiles?: Tile[];
  /** 当前版本的两个选择器，菜单与面包屑都按它们过滤 */
  selectors?: { buildingSystem?: string; gameMode?: string };
  /** 搜索里的「内容」：版本快照里的关卡、卡包…，App 现算 */
  getSearchContent?: ComponentProps<typeof SearchPalette>["getContent"];
  /** 超管标记：服务端给的，用来决定开发向菜单显不显示 */
  isSuperAdmin?: boolean;
  isRoot?: boolean;
  /** App 启动时已拉到的代理，顶栏复用它解析当前 A0，避免重复请求。 */
  agents?: Agent[];
  agent: string | null; onAgent: (id: string | null) => void;
  uiLang: UiLang; onUiLang: (v: UiLang) => void;
  user: string; children: ReactNode;
  onOpenLog: () => void;
  onLogout: () => void;
  /** 后端环境标识，透传给顶栏角标 */
  env?: string;
}) {
  const t = useT();
  const lens = useLens();
  const learningPage = page.startsWith("help-");
  /*
   * 搜索：⌘K / Ctrl+K 任何时候都能开；「/」只在没有在输入框里打字时开（不然输入框里打不出斜杠）
   */
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearching((v) => !v); return; }
      const el = e.target as HTMLElement | null;
      const typing = !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
      if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); setSearching(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // 面包屑按 i18n key 取，不用 nav.ts 里的中文兜底
  // 三级页要显示三段：只在 g.items 里找会漏掉子项，面包屑退化成裸 id
  const nav = navWithTiles(tiles ?? [], selectors ?? {});
  const group = nav.find((g) => g.items.some((i) => i.id === page || i.children?.some((c) => c.id === page)));
  const parent = group?.items.find((i) => i.children?.some((c) => c.id === page));
  // 字典没有的（服务端新加的玩法格）退回菜单项自带的名字
  const leaf = parent?.children?.find((c) => c.id === page);
  const pageName = (() => { const k = `nav.${page}`; const s = t(k as MsgKey); return s === k ? leaf?.label ?? page : s; })();
  const crumbs: string[] = group
    ? isSingle(group)
      ? [pageName]
      : parent
        ? [t(`navGroup.${group.id}` as MsgKey), t(`nav.${parent.id}` as MsgKey), pageName]
        : [t(`navGroup.${group.id}` as MsgKey), pageName]
    : [pageName];
  return (
    /*
     * 布局要点：**最小宽度只加在内容区，不加在最外层**。
     * 加在最外层会让窗口窄于 1280 时整页横向滚动——顶栏和左菜单跟着滚走，
     * 代理选择器被切到屏幕外。运营的笔记本常是 1280 以下，这不是边缘情况。
     *
     * 现在：顶栏 sticky 钉在顶部、左菜单 sticky 钉在左侧，只有右侧内容横向滚。
     */
    <div className="flex min-h-screen flex-col bg-page">
      <SearchPalette open={searching} onClose={() => setSearching(false)} nav={nav} selectors={selectors}
        isSuperAdmin={isSuperAdmin} isRoot={isRoot} onPage={onPage} agent={agent} getContent={getSearchContent} />
      <div className="sticky top-0 z-40">
        <TopBar agent={agent} agents={agents} onAgent={onAgent} uiLang={uiLang} onUiLang={onUiLang} user={user}
          onOpenLog={onOpenLog} onLogout={onLogout} env={env} compactOnNarrow={learningPage} />
      </div>
      {/* 接口视角的面板停靠在右侧：开着时内容区让出它的宽度，表单不被挡 */}
      <div className="flex flex-1 items-stretch" style={lens.on && lens.panel ? { marginRight: `min(${LENS_PANEL_W}px, 94vw)` } : undefined}>
        <div className={`${learningPage ? "hidden md:block " : ""}sticky top-12 z-30 h-[calc(100vh-3rem)] flex-none overflow-y-auto`}>
          <SideNav page={page} onPage={onPage} onSearch={() => setSearching(true)} tiles={tiles} selectors={selectors} isSuperAdmin={isSuperAdmin} isRoot={isRoot} />
        </div>
        <main className="min-w-0 flex-1 overflow-x-auto p-page">
          {/* 中台入口 10-31 关闭：只提示从中台进来的人，让他们在关之前换成账号密码或 Discord */}
          {enteredViaZhongtai() && (
            <div className="mb-3"><Notice tone="warning">{t("shell.zhongtaiSunset", { days: String(zhongtaiSunsetDays()) })}</Notice></div>
          )}
          <div className="mb-3 flex items-center gap-1.5 text-sm text-ink-secondary">
            {crumbs.map((c, i) => (
              <span key={c} className={i === crumbs.length - 1 ? "font-semibold text-ink" : undefined}>
                {i > 0 && <span className="mx-1.5 text-ink-placeholder">/</span>}
                {c}
              </span>
            ))}
            {/* 页头「?」文档入口：映射集中在 nav.ts 的 docUrl，没有登记的页不显示 */}
            {docUrlOf(page) && (
              <a href={docUrlOf(page)} target="_blank" rel="noopener noreferrer" title={t("nav.docLink")}
                className="ml-1 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full border border-line text-[11px] leading-none text-ink-placeholder no-underline hover:text-primary hover:border-primary">?</a>
            )}
            {/* 页头「看视频」：讲这一页的课在 lib/helpVideos.ts 用 hashes 登记，一页可挂多节，按清单顺序排 */}
            {lessonsForPage(page).map((v) => (
              <span key={v.id} className="ml-3 inline-flex font-normal"><HelpVideo id={v.id} variant="watch" /></span>
            ))}
          </div>
          {/* data-collab-*：协同编辑按它换算鼠标位置、在里面找「别人在填的那一格」（lib/collab.ts） */}
          <div className={learningPage ? "min-w-0" : "min-w-[960px]"} data-collab-root data-collab-main>{children}</div>
        </main>
      </div>
    </div>
  );
}
